import _ from 'underscore';
import { zeigeKleineNachricht } from '../systemNachricht.js';
import { stammdatenService } from './StammdatenService.js';

/* eslint-disable no-undef */
class SchichtenService {
	constructor() {
		this.schichtVorlagen = [];
	}

	/**
	 * Holt alle relevanten Schichten der aktuellen Betriebsstätte gefiltert anhand der Parameter aus der DB
	 * @param {Date} laufendesDatum aktueller Montag der angezeigten/gewünschten Kalenderwoche
	 * @param {string} bereichID
	 * @returns Array von Schichten
	 */
	async getSchichten(laufendesDatum, bereichID = undefined) {
		try {
			// wenn kein bestimmter posten gefragt ist, fragen wir jeden Bereich ab
			// Montag
			const startDatum = laufendesDatum.format('YYYY-MM-DD');
			// Sonntag
			const endDatum = laufendesDatum.endOf('week').format('YYYY-MM-DD');
			const url = !bereichID
				? `/dienstplan/api/schichten?StartDatum=${startDatum}&EndDatum=${endDatum}&BetriebsstaetteID=${stammdatenService.aktuelleBetriebsstaette}`
				: `/dienstplan/api/schichten?StartDatum=${startDatum}&EndDatum=${endDatum}&BereichID=${bereichID}&BetriebsstaetteID=${stammdatenService.aktuelleBetriebsstaette}`;
			const response = await fetch(url);
			if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
			}
			const result = await response.json();
			return result;
		} catch (error) {
			console.log(error);
			return [];
		}
	}

	/**
	 * Holt die Monatsstunden (Plan / Plan bis heute / Ist) aller Mitarbeiter der aktuellen Betriebsstätte für die Statuszelle.
	 * @param {string} datum YYYY-MM-DD, bestimmt den Abrechnungsmonat
	 * @returns {object} { Zeitraum: { Monat, Start, Ende }, Mitarbeiter: { [MitarbeiterID]: { StundenGeplant, StundenGeplantBisHeute, StundenErfasst } } }, bei Fehlern ohne Zeitraum
	 */
	async getStundenMonat(datum) {
		try {
			const response = await fetch(`/dienstplan/api/stunden-monat?Datum=${datum}&BetriebsstaetteID=${stammdatenService.aktuelleBetriebsstaette}`);
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				throw new Error(`HTTP error, status = ${response.status}`);
			}
			return await response.json();
		} catch (error) {
			console.log(error);
			return { Mitarbeiter: {} };
		}
	}

	/**
	 * Ruft das komplette Schicht Objekt aus der Datenbank auf
	 * @param {int} schichtID
	 * @returns Serverantwort
	 */
	async holeSchichtObjekt(schichtID) {
		try {
			toggleLoader();
			const response = await fetch(`/dienstplan/api/schichten/${schichtID}`);
			if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
			}
			const result = await response.json();
			toggleLoader();
			return result;
		} catch (error) {
			console.log(error);
			toggleLoader();
			return {};
		}
	}

	/**
	 * Entfernt ein Schichtobjekt von der Datenbank
	 * @param {int} schichtID
	 * @returns Serverantwort
	 */
	async entferneSchichtObjekt(schichtID) {
		try {
			toggleLoader();
			const response = await fetch(`/dienstplan/api/schichten/${schichtID}`, {
				method: 'DELETE',
			});
			if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
			}
			const result = await response.json();
			toggleLoader();
			zeigeKleineNachricht('Schicht erfolgreich gelöscht!', 1);
			return result;
		} catch (error) {
			console.log(error);
	        zeigeKleineNachricht('Fehler beim Löschen der Schicht', -1);
			toggleLoader();
			return false;
		}
	}

	/**
	 * Sendet ein neues Schichtobjekt an den Server und fügt es dort ein oder updatet es
	 * @param {object} schicht
	 * @returns Serverantwort
	 */
	async updateSchichtObjekt(schicht) {
		try {
			toggleLoader();
			// wenn wir eine neue Schicht erstellen, ist eine POST Request
			const istNeu = _.isEmpty(schicht._id);
			let response;
			if (istNeu) {
				response = await fetch('/dienstplan/api/schichten', {
					method: 'POST',
					mode: 'cors',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(schicht),
				});
			} else {
				response = await fetch(`/dienstplan/api/schichten/${schicht._id}`, {
					method: 'PUT',
					mode: 'cors',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(schicht),
				});
			}
			if (!response.ok) {
                const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
			const result = await response.json();
			toggleLoader();
			return result;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Schicht konnte nicht gespeichert werden!', -1);
			toggleLoader();
			return false;
		}
	}

	/**
	 * Sendet ein neues Schichtobjekt an den Server und fügt es dort ein oder updatet es
	 * @param {String} vorlageID
	 * @param {object} schichtInfo
	 * - mitarbeiter
	 * - qualifikation
	 * - posten
	 * - betriebsstaette
	 * @returns Serverantwort
	 */
	async erstelleSchichtObjektAusVorlage(vorlageID, schichtInfo) {
		try {
			toggleLoader();
			const response = await fetch('/dienstplan/api/schichten/schichtvorlage', {
					method: 'POST',
					mode: 'cors',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						VorlageID: vorlageID,
						SchichtInfo: schichtInfo
					}),
				});
			if (!response.ok) {
                const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
			const result = await response.json();
			toggleLoader();
			return result;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Schicht konnte nicht gespeichert werden!', -1);
			toggleLoader();
			return false;
		}
	}


	/**
	 * Holt die Schichtvorlagen für einen bestimmten Bereich oder alle Bereiche
	 * @param {string} bereich ObjectId des Bereichs
	 * @returns
	 */
	async holeSchichtVorlagen(bereichID = undefined) {
		try {
			const url = !bereichID
				? `/dienstplan/api/schichtvorlagen?BetriebsstaetteID=${stammdatenService.aktuelleBetriebsstaette}`
				: `/dienstplan/api/schichtvorlagen?BereichID=${bereichID}&BetriebsstaetteID=${stammdatenService.aktuelleBetriebsstaette}`;
			const response = await fetch(url);
			if (response.ok) {
				const schichten = await response.json();
				return schichten;
			}
			return [];
		} catch (error) {
			console.log(error);
			return [];
		}
	}

	/**
	 * Speichert eine Schichtvorlage in die DB
	 * @param {object} schicht
	 * @returns
	 */
	async speichereSchichtVorlage(schicht) {
		try {
			let methodHTTP = 'PUT';
			 // Bei einer neuen Schichtvorlage müssen wir die schichtID der ausgewählten Vorlage löschen
			 if (schicht._id === '') {
				delete schicht._id;
				methodHTTP = 'POST';
			}
			const response = await fetch('/dienstplan/api/schichtvorlagen', {
				method: methodHTTP,
				headers: {
					'Content-Type': 'application/json',
				},
				mode: 'cors',
				body: JSON.stringify(schicht),
			});
			if (!response.ok) {
                const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
			const result = await response.json();
			zeigeKleineNachricht('Die Schichtvorlage wurde im System vermerkt!', 1);
			return result;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Fehler: Die Schicht konnte nicht vermerkt werden!', -1);
			return false;
		}
	}

	/**
	 * Gibt alle Dienstplan Schichten in dem angegebenen Zeitraum frei...
	 * @param {string} postenID
	 * @param {DateString} datumVon
	 * @param {DateString} datumBis
	 * @param {DateString} betriebsstaetteID
	 */
	async freigabeSchichten(postenID, datumVon, datumBis, betriebsstaetteID) {
		try {
			const body = {
				PostenID: postenID,
				DatumVon: datumVon,
				DatumBis: datumBis,
				BetriebsstaetteID: betriebsstaetteID
			};
			const response = await fetch('/dienstplan/api/schichten/freigabe', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			});
			if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
			}
			zeigeKleineNachricht('Freigabe erfolgreich!', 1);
			return true;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Fehler bei der Freigabe!', -1);
			return false;
		}
	}

	/**
	 * Gibt alle noch nicht freigegebenen Schichten eines ganzen Bereichs frei (nicht nur eines Postens).
	 * @param {string} bereichID
	 * @param {DateString} datumVon
	 * @param {DateString} datumBis
	 * @param {string} betriebsstaetteID
	 */
	async freigabeSchichtenBereich(bereichID, datumVon, datumBis, betriebsstaetteID) {
		try {
			const body = {
				BereichID: bereichID,
				DatumVon: datumVon,
				DatumBis: datumBis,
				BetriebsstaetteID: betriebsstaetteID
			};
			const response = await fetch('/dienstplan/api/schichten/freigabe-bereich', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			});
			if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
			}
			zeigeKleineNachricht('Freigabe erfolgreich!', 1);
			return true;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Fehler bei der Freigabe!', -1);
			return false;
		}
	}

	/**
	 * Gibt alle noch nicht freigegebenen Schichten der angegebenen Mitarbeitenden frei (posten-/
	 * bereichsübergreifend, siehe Mitarbeiteransicht).
	 * @param {array<string>} mitarbeiterIDs
	 * @param {DateString} datumVon
	 * @param {DateString} datumBis
	 * @param {string} betriebsstaetteID
	 */
	async freigabeSchichtenMitarbeiter(mitarbeiterIDs, datumVon, datumBis, betriebsstaetteID) {
		try {
			const body = {
				MitarbeiterIDs: mitarbeiterIDs,
				DatumVon: datumVon,
				DatumBis: datumBis,
				BetriebsstaetteID: betriebsstaetteID
			};
			const response = await fetch('/dienstplan/api/schichten/freigabe-mitarbeiter', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			});
			if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
			}
			zeigeKleineNachricht('Freigabe erfolgreich!', 1);
			return true;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Fehler bei der Freigabe!', -1);
			return false;
		}
	}

	/**
	 * Erstellt eine EGFZ Schicht im Dienstplan (Krank)
	 * @param {object} schichtObjekt
	 * @param {number} egfzID
	 */
	async erstelleEGFZSchicht(schichtObjekt, egfzID) {
		try {
			const response = await fetch('/dienstplan/api/schichten/egfz', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					SchichtInfo: schichtObjekt,
					EgfzID: egfzID
				})
			});
			if (!response.ok) {
                const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
			const result = await response.json();
			return result;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Schicht konnte nicht gespeichert werden!', -1);
			return 500;
		}
	}

	/**
	 * Kopiert eine einzelne Dienstplan-Schicht in eine Zielzelle (anderer Tag/Posten/Mitarbeiter).
	 * @param {string} schichtID ID der Quellschicht
	 * @param {object} schichtInfo Ziel-Mitarbeiter-/Posten-/Bereich-/Datumsinfo
	 * @returns Serverantwort (neue Schicht)
	 */
	async kopiereSchicht(schichtID, schichtInfo) {
		try {
			toggleLoader();
			const response = await fetch('/dienstplan/api/schichten/kopiere-schicht', {
				method: 'POST',
				mode: 'cors',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					SchichtID: schichtID,
					SchichtInfo: schichtInfo,
				}),
			});
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				throw new Error(`HTTP error, status = ${response.status}`);
			}
			const result = await response.json();
			toggleLoader();
			return result;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Schicht konnte nicht kopiert werden!', -1);
			toggleLoader();
			return false;
		}
	}

	/**
	 * Kopiert Schichten aus einer alten KW in eine neue KW für eine Liste an Mitarbeitern.
	 * @param {Date} kwDatumVon Datum der KW der gewünschten Kopierquelle
	 * @param {Date} kwDatumBis Datum der KW des gewünschten Kopierorts
	 * @param {array<string>} mitarbeiterListe
	 */
	async kopiereSchichtenKW(kwVon, kwBis, mitarbeiterListe) {
		try {
			const response = await fetch('/dienstplan/api/schichten/kopieren', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					kwVon,
					kwBis,
					mitarbeiterListe
				})
			});
			if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                return response.status;
			}
			return response.status;
		} catch (error) {
			console.log(error);
			return 500;
		}
	}

	 /**
     * Stellt eine DELETE Request an den Server für die Widerrufung der Urlaubsschichten aus dem Antrag
     * @param {ObjectIdString} mitarbeiterID
     * @param {ObjectIdString} urlaubID
     * @param {String} statusUpdate
     * @returns Urlaubsantrag
     */
	 async entferneUrlaub(mitarbeiterID, urlaubID, statusUpdate) {
        try {
            const body = {
                MitarbeiterID: mitarbeiterID,
                UrlaubID: urlaubID,
                StatusUpdate: statusUpdate
            };
            const response = await fetch('/neolohn/api/mitarbeiter/urlaub', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
				const message = await response.text();
                zeigeKleineNachricht(message, 0);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            zeigeKleineNachricht('Urlaubsantrag erfolgreich abgelehnt!', 1);
			return true;
        } catch (error) {
            console.log(error);
            zeigeKleineNachricht('Fehler bei der Urlaubsablehnung!', -1);
            return false;
        }
    }

    /**
     * Stellt eine POST Request an den Server für die Erstellung der Urlaubsschichten aus dem Antrag
     * @param {ObjectIdString} mitarbeiterID
     * @param {ObjectIdString} urlaubID
     * @param {String} statusUpdate
     */
    async erstelleUrlaubAusAntrag(mitarbeiterID, urlaubID, statusUpdate) {
        try {
            const body = {
                MitarbeiterID: mitarbeiterID,
                UrlaubID: urlaubID,
                StatusUpdate: statusUpdate
            };
            const response = await fetch('/neolohn/api/mitarbeiter/urlaub', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                const msg = await response.text();
                zeigeKleineNachricht(msg);
                throw new Error(`HTTP error, status = ${response.status}`);
            }
            zeigeKleineNachricht('Urlaubsantrag erfolgreich genehmigt!', 1);
			return true;
        } catch (error) {
            console.log(error);
            zeigeKleineNachricht('Fehler bei der Urlaubserstellung!', -1);
            return false;
        }
    }

	async updateSchichtNotiz(schichtID, notiz) {
		try {
			const response = await fetch(`/dienstplan/api/schichten/${schichtID}/notiz`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ SchichtNotiz: notiz })
			});
			if (!response.ok) {
				const message = await response.text();
				zeigeKleineNachricht(message, 0);
				throw new Error(`HTTP error, status = ${response.status}`);
			}
			const result = await response.json();
			zeigeKleineNachricht('Notiz erfolgreich gespeichert!', 1);
			return result;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Fehler beim Speichern der Notiz!', -1);
			return false;
		}
	}
}

const schichtenService = new SchichtenService();
export { schichtenService };
